<template>
  <div :class="['task-input-name', { 'no-x-padding': noXPadding }]">
    <div class="task-input-name__input-wrap">
      <div class="task-input-name__placeholder" v-if="!inputValue">
        <span v-if="!isImportMode">
          Введите название тикета<span class="task-input-name__placeholder-required">*</span>
        </span>
        <span v-else> Название тикета </span>
      </div>
      <div class="task-input-name__input-row">
        <input
          ref="$input"
          class="task-input-name__input"
          v-if="mode !== TaskInputNameModeEnum.View && !isDisabled"
          v-model="value"
          @blur="onBlur"
          @keydown.enter="onClickSave"
        />
        <div class="task-input-name__name" v-else>
          <div
            class="task-input-name__name-content"
            :class="{ 'task-input-name__name-content--fixed': isFixedNameConten }"
            @click="onTriggerToTopScroll"
            @dblclick="onClickEdit"
          >
            {{ value }}
          </div>
          <TirPmTooltipElement :position="TirPmTooltipElementPlacementEnum.Right">
            <TirPmButton
              class="task-input-name__edit-button"
              v-if="mode === TaskInputNameModeEnum.View"
              :variant="TirPmButtonVariantEnum.LinkState"
              :size="TirPmButtonSizeEnum.Small"
              @click="emit('copyName')"
            >
              <template #leftSlot>
                <ClipboardDocumentIcon />
              </template>
            </TirPmButton>
            <template #content>Скопировать в буфер</template>
          </TirPmTooltipElement>
        </div>
        <div
          class="task-input-name__input-right"
          v-if="mode !== TaskInputNameModeEnum.Create && editing && !isDisabled"
        >
          <TirPmTooltipElement
            :position="TirPmTooltipElementPlacementEnum.Left"
            v-if="mode === TaskInputNameModeEnum.View"
          >
            <TirPmButton
              :variant="TirPmButtonVariantEnum.LinkState"
              :size="TirPmButtonSizeEnum.Small"
              @click="onClickEdit"
            >
              <template #leftSlot>
                <PencilSquareIcon />
              </template>
            </TirPmButton>
            <template #content>Редактировать</template>
          </TirPmTooltipElement>
          <template v-else-if="modelValue !== inputValue">
            <TirPmButton
              :variant="TirPmButtonVariantEnum.PrimaryState"
              :size="TirPmButtonSizeEnum.Small"
              @click.prevent="onClickSave"
              @mousedown.prevent
            >
              <template #leftSlot>
                <CheckIcon />
              </template>
            </TirPmButton>
            <TirPmButton
              :variant="TirPmButtonVariantEnum.SecondaryState"
              :size="TirPmButtonSizeEnum.Small"
              @click="onClickCancelSave"
              @mousedown.prevent
            >
              <template #leftSlot>
                <CrossCompactIcon />
              </template>
            </TirPmButton>
          </template>
        </div>
      </div>
    </div>
    <div class="task-input-name__error-message" v-if="errorMessage">
      {{ errorMessage }}
    </div>
  </div>
</template>

<script lang="ts" setup>
import {
  computed,
  inject,
  isReadonly,
  nextTick,
  onMounted,
  onUnmounted,
  PropType,
  ref,
  watch,
} from "vue";
import {
  TirPmButton,
  TirPmButtonVariantEnum,
  TirPmButtonSizeEnum,
  TirPmTooltipElementPlacementEnum,
  TirPmTooltipElement,
} from "tir-components";
import {
  ClipboardDocumentIcon,
  PencilSquareIcon,
  CheckIcon,
  CrossCompactIcon,
} from "tir-style-system/icons/outline";
import { TaskInputNameModeEnum } from "./enums";

const emit = defineEmits<{
  /**Событие обновления значения */
  (e: "update:modelValue", value: string): void;
  /**Событие обовления режима работы */
  (e: "update:mode", mode: TaskInputNameModeEnum): void;
  /**Событие копирования названия задачи */
  (e: "copyName"): void;
}>();

const props = defineProps({
  /**Возможность редактировать */
  editing: {
    type: Boolean as PropType<boolean>,
    default: true,
  },
  /**Значение */
  modelValue: {
    type: String,
    default: "",
  },
  /**Режим работы инпута */
  mode: {
    type: String as PropType<TaskInputNameModeEnum>,
    default: TaskInputNameModeEnum.Create,
  },
  /**Сообщение об ошибке */
  errorMessage: {
    type: String,
  },
  /**Удалить паддинг по x */
  noXPadding: {
    type: Boolean,
    default: false,
  },
  /**Автофокус */
  autoFocus: {
    type: Boolean,
    default: false,
  },
  /**Блокировка */
  isDisabled: {
    type: Boolean,
    default: false,
  },
  /**Режим импорта */
  isImportMode: {
    type: Boolean,
    default: false,
  },
});

/**Ссылка на инпут */
const $input = ref<HTMLInputElement>();

const inputValue = ref(props.modelValue);

watch(
  () => props.modelValue,
  () => {
    inputValue.value = props.modelValue;
  },
);

const onTriggerToTopScroll = () => {
  if (isFixedNameConten.value) {
    document.querySelector(parentScrollContainer).scrollTo({ top: 0, behavior: "smooth" });
  }
};

const onClickEdit = async () => {
  if (!props.isDisabled) {
    emit("update:mode", TaskInputNameModeEnum.Edit);
    await nextTick();
    $input.value?.focus();
  }
};

const onClickSave = async () => {
  emit("update:modelValue", inputValue.value);
  emit("update:mode", TaskInputNameModeEnum.View);
};

const onClickCancelSave = async () => {
  inputValue.value = props.modelValue;
  emit("update:mode", TaskInputNameModeEnum.View);
};

const value = computed({
  get() {
    return inputValue.value;
  },
  set(value) {
    inputValue.value = value;
    if (props.mode === TaskInputNameModeEnum.Create) emit("update:modelValue", value);
  },
});

const onBlur = () => {
  if (props.mode === TaskInputNameModeEnum.Edit) {
    onClickCancelSave();
  }
};

const parentScrollContainer: string = inject("parent-scroll-container");
const isFixedNameConten = ref<boolean>(false);

const checkScrollParentPosition = (e: Event) => {
  let parentContainerScrollTop: HTMLElement = e.target;
  isFixedNameConten.value = parentContainerScrollTop.scrollTop > 290;
};

onMounted(() => {
  if (props.autoFocus) {
    $input.value?.focus();
  }
  document
    .querySelector(parentScrollContainer)
    ?.addEventListener("scroll", checkScrollParentPosition);
});
onUnmounted(() => {
  document
    .querySelector(parentScrollContainer)
    ?.removeEventListener("scroll", checkScrollParentPosition);
});
</script>

<style lang="scss" scoped>
.task-input-name {
  &__name {
    display: flex;
    align-items: center;
  }
  &__input-row {
    display: flex;
    align-items: center;
  }
  &__input-right {
    display: flex;
    align-items: center;
    gap: 0.25rem;
    margin-left: auto;
  }
  &.no-x-padding {
    .task-input-name {
      &__input {
        padding-left: 0;
        padding-right: 0;
      }
      &__placeholder {
        left: 0;
      }
      &__error-message {
        padding-left: 0;
      }
      &__name {
        padding-left: 0;
        padding-right: 0;
      }
    }
  }

  &__input {
    display: block;
    resize: none;
    width: 100%;
    padding: 0.5rem 0.75rem;
    font-family: Roboto, sans-serif;
    font-size: 1.625rem;
    font-weight: 500;
    line-height: 2rem;
    min-height: 3rem;
    outline: none;
    border: none;
    color: var(--text-primary, #272d37);
    overflow: hidden;
    word-wrap: break-word;
  }
  &__name {
    font-size: 1.625rem;
    font-weight: 500;
    line-height: 2rem;
    color: var(--text-primary, #272d37);
    padding: 0.5rem 0 0.5rem 0.75rem;
    width: 100%;
    overflow: hidden;
    text-overflow: ellipsis;
    /* width: 80%; */
    white-space: nowrap;
  }
  &__name-content {
    display: -webkit-box; /* Используем flex-контейнер для ограничения строк */
    -webkit-box-orient: vertical; /* Ориентируем в вертикальном направлении */
    -webkit-line-clamp: 2; /* Ограничиваем до 2 строк */
    overflow: hidden; /* Скрываем текст за пределами блока */
    text-overflow: ellipsis;
    white-space: normal;
    word-break: break-word;
  }
  &__name-content--fixed {
    position: fixed;
    top: 0.75rem;
    left: 9.375rem;
    width: 70vw;
    transition: opacity 0.2s;
    font-size: 1rem;
    cursor: pointer;
  }
  &__error-message {
    color: var(--text-negative, #ec2e14);
    padding: 0 0.75rem;
    font-size: 0.75rem;
    font-weight: 400;
  }
  &__input-wrap {
    position: relative;
  }
  &__placeholder {
    position: absolute;
    left: 0.75rem;
    top: 50%;
    transform: translateY(-50%);
    pointer-events: none;
    font-size: 1.625rem;
    line-height: 2rem;
    color: var(--text-tertiary, #93969b);
    font-weight: 500;
  }
  &__placeholder-required {
    color: var(--text-negative, #ec2e14);
  }
  &__edit-button {
    flex-shrink: 0;
  }
}
</style>
